import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, X } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { api, formatPrice } from "@/lib/api";

export default function SearchModal() {
  const { searchOpen, setSearchOpen } = useStore();
  const [query, setQuery] = useState("");
  const [products, setProducts] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (searchOpen) { api.getProducts().then(setProducts).catch(() => {}); setQuery(""); }
  }, [searchOpen]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return products.filter((p) =>
      p.name.toLowerCase().includes(q) || p.category.toLowerCase().includes(q) ||
      (p.notes || []).some((n) => n.toLowerCase().includes(q))
    );
  }, [query, products]);

  return (
    <AnimatePresence>
      {searchOpen && (
        <motion.div
          data-testid="search-modal"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] bg-cream/95 backdrop-blur-xl flex flex-col"
        >
          <div className="max-w-3xl mx-auto w-full px-6 pt-24">
            <div className="flex items-center justify-between mb-8">
              <p className="eyebrow text-mocha-dark">Caută în colecție</p>
              <button data-testid="search-close-btn" onClick={() => setSearchOpen(false)} className="text-ink hover:text-mocha-dark transition-colors"><X size={26} strokeWidth={1.5} /></button>
            </div>
            <div className="flex items-center gap-4 border-b-2 border-ink pb-4">
              <Search size={26} strokeWidth={1.5} className="text-ink" />
              <input
                data-testid="search-input"
                autoFocus value={query} onChange={(e) => setQuery(e.target.value)}
                placeholder="Nume parfum, notă olfactivă, categorie..."
                className="flex-1 bg-transparent text-2xl sm:text-3xl font-serif text-ink placeholder:text-ink-muted/60 outline-none"
              />
            </div>

            <div className="mt-8 space-y-3 overflow-y-auto max-h-[55vh]">
              {query && results.length === 0 && <p className="text-ink-muted">Niciun rezultat pentru „{query}".</p>}
              {results.map((p) => (
                <button key={p.id} data-testid={`search-result-${p.id}`}
                  onClick={() => { setSearchOpen(false); navigate(`/parfum/${p.id}`); }}
                  className="w-full flex items-center gap-4 p-3 hover:bg-beige/60 transition-colors text-left group">
                  <div className="w-16 h-20 bg-beige overflow-hidden flex-shrink-0">
                    <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="flex-1">
                    <p className="eyebrow text-[0.55rem] text-mocha-dark">{p.brand} · {p.category}</p>
                    <h4 className="font-serif text-xl text-ink">{p.name}</h4>
                    <p className="text-sm text-ink-soft">{formatPrice(p.price)}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
